import { Button, CircularProgress } from '@civilgrid/ui/components';
import { useEffect, useMemo, useState } from 'react';
import { type CivilSearcherResponse, getCivilSearchers, updateCivilSearchers } from '~/api/civilsearch';
import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import { CivilSearchTier } from '~/common/types';
import Checkbox from '~/components2/forms/Checkbox';
import Input from '~/components2/forms/Input';
import Select from '~/components2/forms/Select';
import { Notify } from '~/components2/Notify';
import CivilSearchLayout from '../common/CivilSearchLayout';

interface CivilSearcherAssignments {
  [userId: string]: {
    id: string;
    email: string;
    name: string;
    tiers: Set<CivilSearchTier>;
    title: string;
    phone: string;
    leadId: string | null;
  };
}

export default function CivilSearchersAdmin() {
  const [_civilSearchers, setCivilSearchers] = useState<CivilSearcherResponse[]>([]);
  const [localAssignments, setLocalAssignments] = useState<CivilSearcherAssignments>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const tierDisplay = useMemo(() => {
    const tiers = Object.values(CivilSearchTier) as CivilSearchTier[];
    return tiers;
  }, []);

  // Load civil searchers from API
  useEffect(() => {
    const loadCivilSearchers = async () => {
      try {
        setIsLoading(true);
        const data = await getCivilSearchers();
        setCivilSearchers(data);

        // Initialize local state
        const assignments: CivilSearcherAssignments = {};
        data.forEach((searcher) => {
          assignments[searcher.userId] = {
            id: searcher.id,
            email: searcher.email,
            name: searcher.name,
            tiers: new Set(searcher.tiers),
            title: searcher.title,
            phone: searcher.phone,
            leadId: searcher.leadId,
          };
        });
        setLocalAssignments(assignments);
      } catch (error) {
        console.error('Failed to load civil searchers:', error);
        Notify.error({
          message: 'Failed to load civil searchers',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCivilSearchers();
  }, []);

  // Handle checkbox toggle
  const handleTierToggle = (userId: string, tier: CivilSearchTier) => {
    setLocalAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[userId]) {
        return prev; // Shouldn't happen, but safety check
      }

      const userTiers = new Set(updated[userId].tiers);
      if (userTiers.has(tier)) {
        userTiers.delete(tier);
      } else {
        userTiers.add(tier);
      }

      updated[userId] = {
        ...updated[userId],
        tiers: userTiers,
      };

      return updated;
    });

    setHasChanges(true);
  };

  // Handle title change
  const handleTitleChange = (userId: string, title: string) => {
    setLocalAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[userId]) {
        return prev;
      }

      updated[userId] = {
        ...updated[userId],
        title,
      };

      return updated;
    });

    setHasChanges(true);
  };

  // Handle lead change
  const handleLeadChange = (userId: string, leadId: string | null) => {
    setLocalAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[userId]) {
        return prev;
      }

      updated[userId] = {
        ...updated[userId],
        leadId,
      };

      return updated;
    });

    setHasChanges(true);
  };

  // Handle phone change
  const handlePhoneChange = (userId: string, phone: string) => {
    setLocalAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[userId]) {
        return prev;
      }

      updated[userId] = {
        ...updated[userId],
        phone,
      };

      return updated;
    });

    setHasChanges(true);
  };

  // Handle save operation
  const handleSave = async () => {
    try {
      setIsSaving(true);

      const updates = Object.entries(localAssignments).map(([userId, assignment]) => ({
        userId,
        tiers: Array.from(assignment.tiers),
        title: assignment.title,
        phone: assignment.phone,
        leadId: assignment.leadId,
      }));

      await updateCivilSearchers(updates);

      // Update the original data to reflect saved changes
      const updatedSearchers = Object.entries(localAssignments).map(([userId, assignment]) => ({
        id: assignment.id,
        userId,
        email: assignment.email,
        name: assignment.name,
        tiers: Array.from(assignment.tiers),
        title: assignment.title,
        phone: assignment.phone,
        leadId: assignment.leadId,
        status: 'active',
        createdAt: '',
        updatedAt: '',
      }));
      setCivilSearchers(updatedSearchers);

      setHasChanges(false);
      Notify.info({
        message: 'Civil searchers saved successfully',
      });
    } catch (error: any) {
      console.error('Failed to save civil searchers:', error);
      const reason = error?.response?.data?.reason ?? '';
      const isCycleError =
        reason.toUpperCase().includes('CIRCULAR_REFERENCE_ERROR') || reason.toLowerCase().includes('SELF_LEAD_ERROR');
      Notify.error({
        message: isCycleError ? reason : 'Failed to save civil searchers',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <CivilSearchLayout title="Civil Searchers Management">
        <div className="flex items-center justify-center py-8">
          <CircularProgress indeterminate />
        </div>
      </CivilSearchLayout>
    );
  }

  return (
    <CivilSearchLayout
      title={
        <div className="flex flex-row justify-between items-center w-full">
          <div>Civil Searchers Management</div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            loading={isSaving}
            className={`${
              hasChanges && !isSaving
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          {/* </div> */}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 text-left text-2xs font-medium text-gray-500 uppercase tracking-wider">
                    Civil Searcher
                  </th>
                  <th className="px-6 text-left text-2xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 text-left text-2xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 text-left text-2xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                  {tierDisplay.map((tier) => (
                    <th
                      key={tier}
                      className="px-6 py-3 text-center text-2xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {civilSearchTierLabels[tier]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(localAssignments).map(([userId, assignment]) => (
                  <tr key={userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs font-medium text-gray-900">{assignment.name || assignment.email}</div>
                        <div className="text-xs text-gray-500">{assignment.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="[&>div>div:first-child]:hidden">
                        <Input
                          label=""
                          value={assignment.title}
                          onChange={(value) => handleTitleChange(userId, value)}
                          placeholder="Enter title"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="[&>div>div:first-child]:hidden">
                        <Input
                          label=""
                          value={assignment.phone}
                          onChange={(value) => handlePhoneChange(userId, value)}
                          placeholder="Enter phone"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                      <div className="[&>div>div:first-child]:hidden">
                        <Select
                          label=""
                          isClearable
                          value={assignment.leadId ?? undefined}
                          items={Object.entries(localAssignments)
                            .filter(([otherUserId]) => otherUserId !== userId)
                            .map(([, other]) => ({
                              value: other.id,
                              label: other.name || other.email,
                            }))}
                          onChange={(value) => handleLeadChange(userId, value as string | null)}
                        />
                      </div>
                    </td>
                    {tierDisplay.map((tier) => {
                      const isChecked = assignment.tiers.has(tier);
                      return (
                        <td key={`${userId}-${tier}`} className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex w-full items-center justify-center [&>div]:py-0">
                            <Checkbox
                              label=""
                              checked={isChecked}
                              value={isChecked}
                              onChange={() => handleTierToggle(userId, tier)}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CivilSearchLayout>
  );
}
